import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import type { UserGridView } from '@/types/core/UserGridView';

const BASE_PATH = '/api/core/grid-views';

export const gridViewKeys = {
  all: ['gridViews'] as const,
  byGrid: (gridKey: string) => [...gridViewKeys.all, gridKey] as const,
};

export interface SaveGridViewInput {
  name: string;
  state: string;
  isDefault?: boolean;
}

/** Fetch the current user's saved views for a grid (gridKey = "Module:Entity"). */
export function useGridViews(gridKey: string) {
  return useQuery({
    queryKey: gridViewKeys.byGrid(gridKey),
    queryFn: async () => {
      const { data } = await apiClient.get<UserGridView[]>(BASE_PATH, {
        params: { gridKey },
      });
      return data;
    },
  });
}

/** Create or update (by name) a saved view for a grid. */
export function useSaveGridView(gridKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveGridViewInput) => {
      const { data } = await apiClient.post<UserGridView>(BASE_PATH, input, {
        params: { gridKey },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gridViewKeys.byGrid(gridKey) });
    },
  });
}

/** Rename a saved view. */
export function useRenameGridView(gridKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data } = await apiClient.put<UserGridView>(`${BASE_PATH}/${id}/rename`, { name });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gridViewKeys.byGrid(gridKey) });
    },
  });
}

/** Delete a saved view. */
export function useDeleteGridView(gridKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${BASE_PATH}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gridViewKeys.byGrid(gridKey) });
    },
  });
}

/** Mark a saved view as the user's default for its grid. */
export function useSetDefaultGridView(gridKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`${BASE_PATH}/${id}/set-default`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gridViewKeys.byGrid(gridKey) });
    },
  });
}

/** Clear the default flag on a view, so the system default opens for the grid. */
export function useClearDefaultGridView(gridKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`${BASE_PATH}/${id}/clear-default`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gridViewKeys.byGrid(gridKey) });
    },
  });
}
